import jwtDecode from "jwt-decode";
import { BehaviorSubject } from "rxjs";
import api, { IDataRequest, IDataResponse, STATUS_CODE } from "../../providers/api";
import { LocalStorage } from "../../commons/LocalStorage"
import { ILoginStore, LoginType, UserType } from "./login.types"
import { EmptyResponseException, InvalidException, ParameterRequiredException } from "src/framework/exceptions/Error";
import { appStore, Error } from "../AppStore";

import { ExtraInfoUsuario } from "src/providers/cadastros/ContaProvider/types";
import { authUser$ } from "src/framework/components/navigations/MenuUser";
import { getContaUsuario, getTermos } from "src/store/global.store";
import { notifierStore } from "../NotifierStore";
import { generatePkcePair } from "src/framework/commons/PkceUtil";

export const isLoggedIn$ = new BehaviorSubject<boolean>(false);
const userInfo$ = new BehaviorSubject<UserType & ExtraInfoUsuario | null>(null);
const token$ = new BehaviorSubject<string | null>(null);
export const processandoToken$ = new BehaviorSubject<boolean | null>(null);

export class LoginStoreClass implements ILoginStore {
    
    private permissoes$ = new BehaviorSubject<string[]>([]);
    
    isLoggedInSubject = (): BehaviorSubject<boolean> => isLoggedIn$;
    tokenSubject = (): BehaviorSubject<string | null> => token$;
    userInfoSubject = (): BehaviorSubject<UserType & ExtraInfoUsuario | null> => userInfo$;
    
    get permissoes() {
        return this.permissoes$.asObservable();
    }

    isLoggedIn = (): boolean => {
        return isLoggedIn$.getValue();
    }

    setLoggedIn(loggedIn: boolean): void {
        isLoggedIn$.next(loggedIn);
    }

    userInfo = (): UserType & ExtraInfoUsuario | null => {
        return userInfo$.getValue();
    }

    setUserInfo(userInfo: UserType & ExtraInfoUsuario | null): void {
        userInfo$.next(userInfo);
    }

    token = (): string | null => {
        return token$.getValue();
    }

    setToken(token: string | null): void {
        token$.next(token);
    }

    setPermission(permissoes: string[]): void {
        this.permissoes$.next(permissoes);
    }

    getPermission() {
        return this.permissoes$.getValue();
    }

    validateLogin(tokenRedirect?: string | null): string | null {
        try {
            appStore.setLoading(true);
            const token: string | null = tokenRedirect || LocalStorage.get("solar.token");
            // se encontrar o token e ainda for valido faz o login persistido
            if (token) {
                this.setToken(token)
                LocalStorage.set("solar.token", token);
                if (this.userInfo() === null) {
                    const decodedToken: any = jwtDecode(token);
                    this.setUserInfo(decodedToken.user)
                    LocalStorage.set("solar.userInfo", decodedToken.user, true);
                    this.parseUserData()
                }

                this.getFuncionalidadesUsuario(token)

                this.setLoggedIn(true)
                return token;
            }
            return null;
        } catch (error) {
            //   localStorage.removeItem("solar.auth");
            //   localStorage.removeItem("solar.userInfo");
            return null;
        } finally {
            appStore.setLoading(false);
        }
    }

    async getFuncionalidadesUsuario(token?: string): Promise<string[]> {
        try {
    
          const response = await api.get({
            url: "/funcionalidades/usuario",
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
    
          this.setPermission(response.data || [])
    
          return response.data || []
        } catch (error) {
          return [];
        }
      }

    async loginFromRedirect(token: string): Promise<string> {
        const origin = window.location.origin.toString()
        const lastChar = origin[origin.length - 1]
        const newOrigin = lastChar === "/" ? origin : `${origin}/`

        const request: IDataRequest = {
            url: "/login/token/external",
            data: {
                logout_redirect_uri: `${newOrigin}signout`
            },
            headers: {
                "Authorization": `Bearer ${token}`
            }
        }

        try {
            const response: IDataResponse = await api.post(request);

            if (response.statusCode === STATUS_CODE.BAD_REQUEST) {
                throw new InvalidException(Array.isArray(response.message) ? response.message[0] : response.message, response.headers);
            }

            if (response.statusCode !== STATUS_CODE.OK) {
                if (response.statusCode === STATUS_CODE.INTERNAL_SERVER_ERROR) {
                    throw new EmptyResponseException(
                        "Erro ao tentar efetuar o login, serviço indisponível"
                    );
                }
            }
            
            if (response.statusCode === STATUS_CODE.OK) {
                const decodedToken: any = jwtDecode(response.data.token);
                LocalStorage.set("solar.token", response.data.token);
                this.setUserInfo(decodedToken.user)
                LocalStorage.set("solar.userInfo", decodedToken.user, true);
                this.parseUserData();
                await this.getFuncionalidadesUsuario(response.data.token)
                this.setLoggedIn(true);
                return ""
            }

            return "Erro ao realizar login."
        } catch (error: any) {
            window.history.pushState({}, document.title, window.location.pathname);
            notifierStore.setNotifier({
                title: "Aviso!!!",
                content: error["message"],
                onFinishTimeout: () => {
                    notifierStore.setOpen(false)
                    if (error.headers?.["conta-post-logout-redirect-url"]) window.location.href = error.headers["conta-post-logout-redirect-url"]
                },
                onClose: async () => {
                    notifierStore.setOpen(false)
                    if (error.headers?.["conta-post-logout-redirect-url"]) window.location.href = error.headers["conta-post-logout-redirect-url"]
                },
                timeOut: 10000
            })
            notifierStore.setOpen(true)
            return error["message"] || "Erro ao realizar login"
        }
    }

    async doLoginConta(code: string, sessionState: string, redirectUri: string, codeVerifier: string): Promise<string> {
        if (!code || !sessionState || !redirectUri || !codeVerifier) return "Erro ao tentar efetuar o login."

        const redirectUriLocal = redirectUri.endsWith("/") ? redirectUri.slice(0, -1) : redirectUri

        try {
            
            const response = await api.post({
                url: '/login/conta',
                data: {
                  code,
                  session_state: sessionState,
                  uri: redirectUriLocal,
                  code_verifier: codeVerifier,
                  logout_redirect_uri: `${window.location.origin}/signout`
                }
              })

            if (response.statusCode === STATUS_CODE.BAD_REQUEST) {
                throw new InvalidException(Array.isArray(response.message) ? response.message[0] : response.message, response.headers);
            }

            if (response.statusCode !== STATUS_CODE.OK) {
                if (response.statusCode === STATUS_CODE.INTERNAL_SERVER_ERROR) {
                    throw new EmptyResponseException(
                        "Erro ao tentar efetuar o login, serviço indisponível"
                    );
                }
            }

            if (Object.keys(response.data).indexOf("token") < 0) {
                throw new EmptyResponseException(
                    "Erro ao tentar efetuar o login, sem resposta do serviço"
                );
            }

            const decodedToken: any = jwtDecode(response.data.token);
            this.setToken(response.data.token)

            await this.getFuncionalidadesUsuario(response.data.token)

            this.setLoggedIn(true);
            LocalStorage.set("solar.token", response.data.token);
            
            this.setUserInfo(decodedToken.user)
            
            LocalStorage.set("solar.userInfo", decodedToken.user, true);
            this.parseUserData();
            this.setLoggedIn(true);

            // Busca informações usuário
            getTermos();
            getContaUsuario();

            return ""

        } catch (error: any) {
            window.history.pushState({}, document.title, window.location.origin);
            notifierStore.setNotifier({
                title: "Aviso!!!",
                content: error["message"],
                onFinishTimeout: () => {
                    notifierStore.setOpen(false)
                    if (error.headers?.["conta-post-logout-redirect-url"]) window.location.href = error.headers["conta-post-logout-redirect-url"]
                },
                onClose: async () => {
                    notifierStore.setOpen(false)
                    if (error.headers?.["conta-post-logout-redirect-url"]) window.location.href = error.headers["conta-post-logout-redirect-url"]
                },
                timeOut: 10000
            })
            notifierStore.setOpen(true)
            return error["message"] || "Erro ao realizar login"
        }
    }

    async getContaUrlCompleto(redirectUri: string): Promise<string> {
        try {
            let redirectUriLocal = redirectUri.includes("signout") ? window.location.origin : redirectUri
            redirectUriLocal = redirectUriLocal.endsWith("/") ? redirectUriLocal.slice(0, -1) : redirectUriLocal

            const { codeChallenge, codeVerifier } = await generatePkcePair()
            if (!codeChallenge) {
                throw new InvalidException("Erro ao gerar code challenge")
            }

            const contaUrl = await LoginStore.getContaUrl(redirectUriLocal, codeChallenge)

            if (!contaUrl) {
                throw new InvalidException("Erro ao gerar conta url")
            }

            LocalStorage.set('solar.code_verifier', codeVerifier)
            return contaUrl
        } catch (error: any) {
            return ""
        }
    }

    async getContaUrl(redirectUri: string, codeChallenge: string): Promise<string> {
        const contaUrlResult = await api.get({
          url: `/login/conta?uri=${redirectUri}&code_challenge=${codeChallenge}`,
        })
        return contaUrlResult.data.redirect
    }

    async doLogin({ username, password }: LoginType): Promise<void> {
        try {

            if (password === "" || username === "") {
                throw new ParameterRequiredException(
                    "Usuário ou senha estão em branco"
                );
            }
            // efetua o login na pla
            const response: IDataResponse = await api.post({
                url: "/login",
                data: { username: username, password: password },
                headers: {
                    Accpet: "*/*",
                },
            });

            if (response.statusCode === STATUS_CODE.BAD_REQUEST) {
                throw new InvalidException((response.message as any)?.join(", "));
            }

            if (response.statusCode !== STATUS_CODE.OK) {
                if (response.statusCode === STATUS_CODE.INTERNAL_SERVER_ERROR) {
                    throw new EmptyResponseException(
                        "Erro ao tentar efetuar o login, serviço indisponível"
                    );
                }
            }

            if (Object.keys(response.data).indexOf("token") < 0) {
                throw new EmptyResponseException(
                    "Erro ao tentar efetuar o login, sem resposta do serviço"
                );
            }
            // decode jwt
            const decodedToken: any = jwtDecode(response.data.token);
            this.setToken(response.data.token)

            await this.getFuncionalidadesUsuario(response.data.token)
            
            this.setLoggedIn(true);
            LocalStorage.set("solar.token", response.data.token);
            
            this.setUserInfo(decodedToken.user)
            LocalStorage.set("solar.userInfo", decodedToken.user, true);
            this.parseUserData();
            this.setLoggedIn(true);
            
            // Busca informações usuário
            getTermos();
            getContaUsuario();

        } catch (error: any) {
            let _error: Error = {
                error: "",
                code: error.code,
                message: error["message"],
            };
            appStore.setError(_error.message);
        }
    }

    doLogOut(): void {
        LocalStorage.removeAll()
        this.setLoggedIn(false);
        this.setPermission([]);
    }

    async doLogOutConta(): Promise<string | null | undefined> {
        let redirectURL: string | null | undefined = null
        try {
          const origin = window.location.origin.toString()
          const lastChar = origin[origin.length - 1]
          const newOrigin = lastChar === "/" ? origin : `${origin}/`
    
          const request: IDataRequest = {
              url: "/logout/conta",
              data: {
                  redirect_uri: `${newOrigin}signout`
              }
          }
    
          const responseConta = await api.post(request);
    
          if (responseConta.statusCode !== STATUS_CODE.OK) {
              throw new InvalidException(responseConta.message)
          }
          redirectURL = responseConta?.data?.redirect_uri
        } catch (error) {
            const messages: string[] = error instanceof InvalidException ? [error.message] : [error as string]
    
            notifierStore.setNotifier({
                title: "Aviso!!!",
                content: messages,
                onClose: () => notifierStore.setOpen(false),
                timeOut: 2500
            })
    
            notifierStore.setOpen(true)
        }
    
        return redirectURL;
    }

    async parseUserData(): Promise<void> {

        if (this.userInfo()?.userId) {

            const token = LocalStorage.get("solar.token")
            if (token) {
                const oldUserExtraInfo = LocalStorage.get("solar.userExtraInfo", true)

                const decodedToken: any = jwtDecode(token)

                const fields = Object.keys(decodedToken as any);

                const userExtraInfo: ExtraInfoUsuario = {
                    isIntelbras: false,
                    isDistribuidor: false,
                    isRevenda: false,
                    codigo_servico_conta: null,
                };

                if (fields.includes('custom')) {
                    if (decodedToken?.custom?.revenda?.id !== null && decodedToken?.custom?.distribuidor?.id !== null) {
                        userExtraInfo.revenda_id = decodedToken?.custom?.revenda?.id;
                        userExtraInfo.revenda_nome = decodedToken?.custom?.revenda?.nome;
                        userExtraInfo.revenda_cnpj_cpf = decodedToken?.custom?.revenda?.cnpj_cpf;
                        userExtraInfo.distribuidor_id = decodedToken?.custom?.distribuidor?.id;
                        userExtraInfo.distribuidor_nome = decodedToken?.custom?.distribuidor?.nome;
                        userExtraInfo.distribuidor_cnpj_cpf = decodedToken?.custom?.distribuidor?.cnpj_cpf;
                        userExtraInfo.isRevenda = true
                        userExtraInfo.isDistribuidor = false
                        userExtraInfo.isIntelbras = false
                    }

                    if (decodedToken?.custom?.distribuidor?.id && decodedToken?.custom?.revenda?.id === null) {
                        userExtraInfo.revenda_id = null;
                        userExtraInfo.revenda_nome = null;
                        userExtraInfo.revenda_cnpj_cpf = null;
                        userExtraInfo.distribuidor_id = decodedToken?.custom?.distribuidor?.id;
                        userExtraInfo.distribuidor_nome = decodedToken?.custom?.distribuidor?.nome;
                        userExtraInfo.distribuidor_cnpj_cpf = decodedToken?.custom?.distribuidor?.cnpj_cpf;
                        userExtraInfo.isRevenda = false
                        userExtraInfo.isDistribuidor = true
                        userExtraInfo.isIntelbras = false
                    }

                    // identifica usuario intelbras
                    if (!decodedToken?.custom?.distribuidor?.id && !decodedToken?.custom?.revenda?.id) {
                        userExtraInfo.revenda_id = null;
                        userExtraInfo.revenda_nome = null;
                        userExtraInfo.revenda_cnpj_cpf = null;
                        userExtraInfo.distribuidor_id = null;
                        userExtraInfo.distribuidor_nome = null;
                        userExtraInfo.distribuidor_cnpj_cpf = null;
                        userExtraInfo.isIntelbras = true
                        userExtraInfo.isRevenda = false
                        userExtraInfo.isDistribuidor = false
                    }

                    if(!userExtraInfo?.distribuidor_cnpj_cpf && oldUserExtraInfo?.distribuidor_id && oldUserExtraInfo?.distribuidor_cnpj_cpf && oldUserExtraInfo?.distribuidor_nome){
                        userExtraInfo.distribuidor_id = oldUserExtraInfo?.distribuidor_id;
                        userExtraInfo.distribuidor_nome = oldUserExtraInfo?.distribuidor_nome;
                        userExtraInfo.distribuidor_cnpj_cpf = oldUserExtraInfo?.distribuidor_cnpj_cpf;
                    }
                    if(!userExtraInfo?.revenda_cnpj_cpf && oldUserExtraInfo?.revenda_id && oldUserExtraInfo?.revenda_cnpj_cpf && oldUserExtraInfo?.revenda_nome && (userExtraInfo?.distribuidor_cnpj_cpf && userExtraInfo?.distribuidor_cnpj_cpf === oldUserExtraInfo?.distribuidor_cnpj_cpf)){
                        userExtraInfo.revenda_id = oldUserExtraInfo?.revenda_id;
                        userExtraInfo.revenda_nome = oldUserExtraInfo?.revenda_nome;
                        userExtraInfo.revenda_cnpj_cpf = oldUserExtraInfo?.revenda_cnpj_cpf;
                    }
                } else {
                    userExtraInfo.revenda_id = null;
                    userExtraInfo.revenda_nome = null;
                    userExtraInfo.revenda_cnpj_cpf = null;
                    userExtraInfo.distribuidor_id = null;
                    userExtraInfo.distribuidor_nome = null;
                    userExtraInfo.distribuidor_cnpj_cpf = null;
                    userExtraInfo.isIntelbras = true
                    userExtraInfo.isRevenda = false
                    userExtraInfo.isDistribuidor = false
                }
                if (this.isLoggedIn()) {
                    const informacoes = await this.getUserInfo();
                    if (informacoes.statusCode === STATUS_CODE.OK) {
                        if (informacoes?.data?.[0]) {
                            userExtraInfo.role = informacoes?.data?.[0]?.role || "Não definido";
                            userExtraInfo.codigo_servico_conta = informacoes?.data?.[0]?.codigo_servico_conta || null;
                        }
                    }
                    authUser$.next(userExtraInfo);
                }
                parseExtraInfo(userExtraInfo)
            }
        }
    }

    isIntelbras = (): boolean => {
        return this.userInfo()?.isIntelbras || false
    };

    isRevenda = (): boolean => {
        return this.userInfo()?.isRevenda || false
    }
    isDistribuidoor = (): boolean => {
        return this.userInfo()?.isDistribuidor || false;
    }


    async getUserInfo(): Promise<IDataResponse> {
        try {
            const response: IDataResponse = await api.get({
                url: `/usuario/informacoes`,
            });

            return response;   
        
        } catch (error: any) {
            return {
                data: [],
                message: error.message,
                statusCode: 400
            }
        }
    }


}

export const parseExtraInfo = (response: any) => {
    if (response) {
        const userInfo: any = {
            ...userInfo$.getValue(),
            ...response
        }
        LocalStorage.set("solar.userExtraInfo", userInfo, true)
    }
}

export const LoginStore = new LoginStoreClass();